import { logger } from 'shared/lib'
import { ComplianceApi } from 'entities/Compliance/api/compliance'
import { deriveComplianceTiers } from 'entities/Compliance/lib/complianceTiers'
import { RcsApi } from 'entities/Rcs/api'
import {
  deriveComplianceStatus,
  type ComplianceSignals,
  type ComplianceStatus,
} from 'entities/WebMcp/lib/complianceSignals'

// Each source degrades to `null` (→ `unknown`) so one flaky endpoint never blocks the
// answer. Pass the bare error: logger.error drops cancellations via isCancel, which a
// wrapping object would defeat.
const safe = async <T>(read: () => Promise<T>): Promise<T | null> => {
  try {
    return await read()
  } catch (error) {
    logger.error(error)
    return null
  }
}

interface FetchOptions {
  /** RCS is hidden unless the organization has the feature. */
  rcsAvailable: boolean
  isUKCountry: boolean
}

export const fetchComplianceSignals = async ({
  rcsAvailable,
  isUKCountry,
}: FetchOptions): Promise<ComplianceSignals> => {
  const [summaryRead, businessProfile, services, tollFree, rcs] = await Promise.all([
    safe(async () => (await ComplianceApi.getComplianceSummary()).data),
    safe(async () => {
      const { data } = await ComplianceApi.getBusinessProfileData()
      return {
        status: data.businessProfile?.status ?? null,
        // Matches BusinessProfile.isStatusInProgress.
        hasProgress: Boolean(data.businessInformation?.reg_progress_status),
        submittedAt: data.businessProfile?.submitted_at ?? null,
        // Same nested read complianceStore uses for log_activity.
        hasUnverifiedTollFree: Boolean(data.businessProfile?.organization?.has_unverified_tollfree),
      }
    }),
    safe(async () => {
      const { data } = await ComplianceApi.getComplianceServicesList()
      // First campaign is the org's, matching ComplianceServicesList.campaign.
      const campaign = data.campaigns[0] ?? null
      return {
        brand: {
          status: data.brand?.status ?? null,
          submittedAt: data.brand?.submitted_at ?? null,
        },
        campaign: { status: campaign?.status ?? null, submittedAt: campaign?.submitted_at ?? null },
      }
    }),
    safe(async () => {
      const { data } = await ComplianceApi.getOrganizationTollFree()
      return {
        numberStatuses: data.data.flatMap((profile) =>
          profile.numbers.map((number) => number.verified_status)
        ),
      }
    }),
    rcsAvailable
      ? safe(async () => {
          const { data } = await RcsApi.getRcs()
          return { status: data[0]?.compliance_status ?? null }
        })
      : null,
  ])

  // Two sources for one fact, so a failed profile read cannot downgrade toll-free
  // while the toll-free read is sitting on `Unverified` numbers.
  const hasUnverifiedTollFree =
    businessProfile?.hasUnverifiedTollFree === true ||
    tollFree?.numberStatuses.includes('Unverified') === true

  const summary = summaryRead && {
    // Mirrors ComplianceStore.isQuestionnaire.
    questionnaireDone: Boolean(summaryRead.organization_id),
    tiers: deriveComplianceTiers({
      targetCountry: summaryRead.target_country,
      hasTaxId: summaryRead.has_tax_id,
      numbersType: summaryRead.numbers_type,
      isUKCountry,
      hasUnverifiedTollFree,
    }),
  }

  return {
    summary,
    businessProfile,
    brand: services?.brand ?? null,
    campaign: services?.campaign ?? null,
    tollFree,
    rcs,
    rcsAvailable,
  }
}

export const computeComplianceStatus = async (options: FetchOptions): Promise<ComplianceStatus> =>
  deriveComplianceStatus(await fetchComplianceSignals(options))
